import IconButton from '@ds/components/actions/IconButton';
import Box from '@ds/components/layout/Box';
import Divider from '@ds/components/layout/Divider';
import Flex from '@ds/components/layout/Flex';
import { CloseIcon } from '@ds/icons';
import {
    FloatingFocusManager,
    FloatingOverlay,
    FloatingPortal,
    useDismiss,
    useFloating,
    useInteractions,
    useRole,
    useTransitionStatus,
} from '@floating-ui/react';
import React from 'react';

import Brand from './Brand';
import LogoutButton from './LogoutButton';
import styles from './MobileNav.module.css';
import UserCard from './UserCard';

/** Matches `--duration-normal`, the slide in MobileNav.module.css, so the drawer unmounts once it has slid out. */
const TRANSITION_MS = 200;

interface Props {
    menuOpen: boolean;
    onClose: () => void;
    isLoggedIn: boolean;
    hasUser: boolean;
    children: React.ReactNode;
}

/**
 * The navigation drawer below BP_DESKTOP (1200). It is a modal dialog: while open, focus moves to Close and stays
 * inside it, Escape or a press on the backdrop closes it, the page behind does not scroll, and focus returns to the
 * Menu button on close. While closed it is not rendered, so none of its controls are in the Tab order or the
 * accessibility tree (they were tabbable behind a 0px-wide panel before).
 */
const MobileNav: React.FC<Props> = ({ menuOpen, onClose, hasUser, children }) => {
    const { refs, context } = useFloating({
        open: menuOpen,
        onOpenChange: (open) => {
            if (!open) {
                onClose();
            }
        },
    });
    const { getFloatingProps } = useInteractions([useDismiss(context), useRole(context, { role: 'dialog' })]);
    const { isMounted, status } = useTransitionStatus(context, { duration: TRANSITION_MS });

    if (!isMounted) {
        return null;
    }

    // Choosing a destination (a nav item, Log out) closes the drawer, as it always has. A press anywhere else
    // inside the drawer no longer does.
    const closeOnActivate = (event: React.MouseEvent<HTMLElement>): void => {
        if ((event.target as Element).closest('a, button')) {
            onClose();
        }
    };

    return (
        <FloatingPortal>
            <FloatingOverlay
                className={styles.overlay}
                data-status={status}
                lockScroll
            >
                <FloatingFocusManager
                    context={context}
                    modal
                >
                    <div
                        ref={refs.setFloating}
                        className={styles.container}
                        data-status={status}
                        aria-label="Menu"
                        {...getFloatingProps({ onClick: closeOnActivate })}
                    >
                        <Box p={8}>
                            <Flex
                                direction="column"
                                justify="between"
                                gap={8}
                                height="100%"
                            >
                                <Flex
                                    direction="column"
                                    gap={4}
                                >
                                    <Flex
                                        direction="row"
                                        align="center"
                                        gap={3}
                                    >
                                        <IconButton
                                            icon={<CloseIcon />}
                                            label="Close menu"
                                            variant="text"
                                            shape="circle"
                                        />
                                        <Brand />
                                    </Flex>
                                    <Divider />
                                    <Flex
                                        as="nav"
                                        aria-label="Main"
                                        direction="column"
                                        align="start"
                                        gap={2}
                                    >
                                        {children}
                                    </Flex>
                                </Flex>
                                <Flex
                                    direction="column"
                                    align="start"
                                    gap={8}
                                >
                                    {hasUser && <UserCard />}
                                    <LogoutButton />
                                </Flex>
                            </Flex>
                        </Box>
                    </div>
                </FloatingFocusManager>
            </FloatingOverlay>
        </FloatingPortal>
    );
};

export default MobileNav;
